import * as ng from 'angular';
import * as uib from 'angular-ui-bootstrap';
import * as _ from 'underscore';

import {Experience, ExperienceTypeDef, AppDeviceID} from '../../';
import {EditorModel} from '../../editor/editor.model';
import {EditorPageModel} from '../../editor/editor-page.model';
import {appTemplates, AppStepType, appPages} from './app-templates';
import { L10nService, UtilsService } from '../../../../shared';
import './app-add-step.controller';
import './select-filters.controller';

const addStepViewUrl = require('./app-add-step.html');
const setupViewUrl = require('./app-setup.html');
const settingsViewUrl = require('./app-settings.html');
const selectFiltersViewUrl = require('./select-filters.html');

ng.module('e360')
   .factory('appEditorModelFactory', appEditorModelFactory);

appEditorModelFactory.$inject = ['instanceServiceFactory'];
function appEditorModelFactory(instanceServiceFactory) {
   return instanceServiceFactory(AppEditorModel);
}

export interface AppEditorModelFactory {
   ({campaign, typeDef:ExperienceTypeDef, experience:Experience}):AppEditorModel
}

export class AppEditorModel extends EditorModel {
   readonly workspaceClass:string;
   private _pages:AppPageModel[];

   readonly settingsView = settingsViewUrl;
   readonly setupView = setupViewUrl;
   templates = appTemplates;
   externalDataProviders;

   static $inject = ['session', 'gettextCatalog', 'l10n', '$uibModal', 'utils']
      // separating instance args from injected services
      .concat(['campaign', 'typeDef', 'experience']);

   constructor(
      private session,
      private gettextCatalog,
      private l10n:L10nService,
      public $uibModal:uib.IModalService,
      public utils:UtilsService,
      campaign, typeDef:ExperienceTypeDef, experience:Experience
   ) {
      super(campaign, typeDef, experience);

      this.hasPages = true;
      this.hasSetup = true;
      this.setupComplete = !this.isNew;
      this.hasSettings = true;

      this.fonts = this.fontService.appFonts;
      
      // TO DO: load externalDataProviders from Brand Settings
      let org = this.session.getOrg();
      this.externalDataProviders = org.slug == 'brown-forman' ? ['jd-squire'] : [];

      this.workspaceClass = `device app app-${this.typeDef.device}`;

      let brandSettings = this.session.getBrand().settings.experience;
      let defaults = {
         device: typeDef.device,
         backgroundColor: brandSettings.backgroundColor,
         buttonColor: brandSettings.buttonColor,
         buttonTextColor: brandSettings.buttonTextColor,
         font: brandSettings.appFont,
         foregroundColor: brandSettings.fontColor,
         unselectedButtonColor: brandSettings.buttonColorUnselected,
         unselectedButtonTextColor: brandSettings.buttonTextColorUnselected,
         labelFontSize: 14,
         paddingTop: typeDef.device == AppDeviceID.Phone ? 30 : 40,
         paddingLeft: 10,
         paddingRight: 10,
         steps: [],
         uploads: { background: {} }
      };

      if (this.isNew) {
         this.experience = {
            campaignId: campaign.id,
            type: typeDef.id,
            settings: ng.copy(defaults)
         }

         let bgImageKey = typeDef.device == AppDeviceID.Phone ? 'iPhoneBackground' : 'iPadBackground';
         if (brandSettings.uploads[bgImageKey]) {
            this.experience.settings.uploads.background = ng.copy(brandSettings.uploads[bgImageKey]);
         }
      } else {
         this.setThankyouText(_.last(this.experience.settings.steps));
      }

      this.createPages();

      this.templates.regVideo.invalid = !this.hasYoutube;
      this.templates.checkInRegVideo.invalid = !this.hasYoutube;
   }

   onAddPage() {
      let modal = this.$uibModal.open({
         size: 'md',
         backdrop: 'static',
         templateUrl: addStepViewUrl,
         controller: 'AppAddStepController as ctrl',
         resolve: {
            model: () => this
         }
      });

      modal.result.then((page) => this.addPage(page));
   }

   addPage(page) {
      // copy the page template so we don't mutate defaults
      let step = ng.copy(appPages[page.type]);

      if (step.type == AppStepType.Form) {
         // clear default fields of form template
         step.customFields = [];
      }

      let insert = this.getInsertIndex(step);
      this.experience.settings.steps.splice(insert, 0, step)
      this.pages.splice(insert, 0, new AppPageModel(this));
   }

   private getInsertIndex(step):number {
      let insertAfter = (...types:AppStepType[]) => {
         for (let type of types) {
            let index = _.findLastIndex(this.pages, p => p.settings.type == type);
            if (index !== -1) {
               return index + 1;
            }
         }
         return 0;
      }

      if (step.type == AppStepType.Languages) {
         return 0;
      }

      if (step.type == AppStepType.CheckIn || step.type == AppStepType.QRScan) {
         return insertAfter(AppStepType.Languages);
      }

      if (step.type == AppStepType.Form) {
         return insertAfter(AppStepType.Form, AppStepType.CheckIn,
            AppStepType.QRScan, AppStepType.Languages);
      }

      if (step.type == AppStepType.QRAssign) {
         // QRAssign only allowed when CheckIn is present
         return insertAfter(AppStepType.Form, AppStepType.CheckIn);
      }

      if (step.type == AppStepType.Photo || step.type == AppStepType.Video) {
         return insertAfter(AppStepType.QRAssign, AppStepType.Form,
            AppStepType.CheckIn, AppStepType.QRScan, AppStepType.Languages);
      }
   }

   deletePage(page:AppPageModel) {
      let pageIndex = this.pages.indexOf(page);
      this.experience.settings.steps.splice(pageIndex, 1);
      this.pages.splice(pageIndex, 1);
   }

   sortPage(dragIndex:number, dropIndex:number) {
      let tmp = this.experience.settings.steps[dragIndex];
      this.experience.settings.steps[dragIndex] = this.experience.settings.steps[dropIndex];
      this.experience.settings.steps[dropIndex] = tmp;
      tmp = this.pages[dragIndex];
      this.pages[dragIndex] = this.pages[dropIndex];
      this.pages[dropIndex] = tmp;
   }

   get pages() {
      return this._pages;
   }

   createPages() {
      let steps = this.experience.settings.steps;
      this._pages = _.map(steps, () => new AppPageModel(this));
   }

   get hasYoutube() {
      return this.campaign.settings.youTubeRefreshToken;
   }

   private setThankyouText(thankyouPage) {
      let thanyouText;

      if (this.typeDef.device == AppDeviceID.Phone) {
         this.gettextCatalog.getString('<p style="text-align: center;"><span style="font-size: 14pt;">Thanks for stopping by</span><br /><span style="font-size: 14pt;">Enjoy the rest of the event!</span></p>');
         thanyouText = '<p style="text-align: center;"><span style="font-size: 14pt;">Thanks for stopping by</span><br /><span style="font-size: 14pt;">Enjoy the rest of the event!</span></p>';
      } else {
         this.gettextCatalog.getString('<p style="text-align: center;"><span style="font-size: 24pt;">Thanks for stopping by</span><br /><span style="font-size: 24pt;">Enjoy the rest of the event!</span></p>');
         thanyouText = '<p style="text-align: center;"><span style="font-size: 24pt;">Thanks for stopping by</span><br /><span style="font-size: 24pt;">Enjoy the rest of the event!</span></p>';
      }

      // currently users can remove this single field from thankyou page
      // (probably shouldn't be able to?) must check exists to avoid errors
      if (ng.isArray(thankyouPage.customFields) && thankyouPage.customFields.length) {
         this.l10n.translateObject(
            thankyouPage.customFields[0],
            'description',
            thanyouText,
            this.languages
         );
      }
   }

   useTemplate(template) {
      let pages = ng.copy(template.pages) as any[];

      if (this.languages.length > 1) {
         pages.unshift(appPages[AppStepType.Languages]);
      }

      let thankyouPage = pages[pages.length - 1];
      this.setThankyouText(thankyouPage);

      if (this.typeDef.device == AppDeviceID.Phone) {
         this.experience.settings.name = `iPhone ${template.title}`;
      } else {
         this.experience.settings.name = `iPad ${template.title}`;

         let formPage = _.find(pages, (page) => {
            return page.type === AppStepType.Form;
         });

         // Set form fields width to single column
         // and stagger fields across two columns
         if (formPage) {
            formPage.customFields[0].sizeX = 1;

            formPage.customFields[1].sizeX = 1;
            formPage.customFields[1].row = 0;
            formPage.customFields[1].col = 1;

            formPage.customFields[2].sizeX = 1;
            formPage.customFields[2].row = 3;

            formPage.customFields[3].sizeX = 1;
            formPage.customFields[3].row = 3;
            formPage.customFields[3].col = 1;
         }

         thankyouPage.customFields[0].sizeY = 5;
         thankyouPage.customFields[0].minSizeY = 5;
         thankyouPage.customFields[0].row = 15;
      }

      this.experience.settings.steps = pages;
      this.createPages();

      this.setupComplete = true;
   }
}

const pageLanguagesView = require('./page-languages.html');
const pageCheckinView = require('./page-checkin.html');
const pageQRScanView = require('./page-qrscan.html');
const pageQRAssignView = require('./page-qrassign.html');
const pagePhotoView = require('./page-photo.html');
const pageThankyouView = require('./page-thankyou.html');
const pageFormView = require('./page-form.html');
const pageVideoView = require('./page-video.html');

class AppPageModel extends EditorPageModel {
   private backgroundKey:string;

   constructor(editor:AppEditorModel) {
      super(editor);

      this.backgroundKey = editor.utils.generateUniqueId();
   }

   initPhotoSettings() {
      this.getSendTo('photoSettings');
   }

   initVideoSettings() {
      this.getSendTo('videoSettings');
   }

   get backgroundImage():string {
      if (this.settings.backgroundKey) {
         return this.editor.settings.uploads[this.settings.backgroundKey][this.editor.language];
      }

      return null;
   }

   set backgroundImage(image:string) {
      if (!this.settings.backgroundKey) {
         this.settings.backgroundKey = this.backgroundKey;
      }

      if (!this.editor.settings.uploads[this.settings.backgroundKey]) {
         this.editor.settings.uploads[this.settings.backgroundKey] = { };
      }

      let bgUploads = this.editor.settings.uploads[this.settings.backgroundKey] as { [s: string]: string };

      bgUploads[this.editor.language] = image;

      // clear backgroundKey setting after removing bg images for all langs
      if (_.every(bgUploads, (val, key) => !val)) {
         this.settings.backgroundKey = null;
      }
   }

   readonly tinymceOptions = {
      plugins: 'textcolor paste',
      fontsize_formats: "10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt",
      toolbar: 'undo redo | bold underline italic | forecolor fontsizeselect | alignleft aligncenter alignright alignjustify'
   };

   get settings() {
      let myIndex = this.editor.pages.indexOf(this);
      return this.editor.settings.steps[myIndex];
   }

   get view() {
      if (this.settings.type == AppStepType.Languages) {
         return pageLanguagesView;
      } else if (this.settings.type == AppStepType.CheckIn) {
         return pageCheckinView;
      } else if (this.settings.type == AppStepType.QRAssign) {
         return pageQRAssignView;
      } else if (this.settings.type == AppStepType.QRScan) {
         return pageQRScanView;
      } else if (this.settings.type == AppStepType.Photo) {
         return pagePhotoView;
      } else if (this.settings.type == AppStepType.Video) {
         return pageVideoView;
      } else if (this.settings.type == AppStepType.ThankYou) {
         return pageThankyouView;
      } else if (this.settings.type == AppStepType.Form) {
         return pageFormView;
      }
   }

   get sortable() {
      return this.settings.type == AppStepType.Form;
   }

   get style() {
      return {
         'font-family': this.viewSettings.font,
         'padding-left': this.viewSettings.paddingLeft + 'px',
         // -20 for the status bar
         'padding-top': (this.viewSettings.paddingTop - 20) + 'px',
         'padding-right': this.viewSettings.paddingRight + 'px',
         // Background needs color set to avoid user seeing multiple
         // pages stacked on top of each other
         'background-color': this.viewSettings.backgroundColor || '#fff',
         color: this.viewSettings.foregroundColor,
         'background-image': this.backgroundUrl
      };
   }

   get backgroundUrl() {
      let baseBg = this.editor.settings.uploads.background;
      let overrideBg = this.editor.settings.uploads[this.settings.backgroundKey];
      let defaultLanguage = this.editor.campaign.defaultLanguage;

      let url:string;
      if (overrideBg && (overrideBg[this.editor.language] || overrideBg[defaultLanguage])) {
         url = overrideBg[this.editor.language] || overrideBg[defaultLanguage];
      } else if (baseBg && Object.keys(baseBg).length > 0) {
         url = baseBg[this.editor.language] || baseBg[defaultLanguage];
      }

      return url ? 'url(' + url + ')' : 'none';
   }

   hasUpload(id:string) {
      return this.editor.settings.uploads[id] && this.editor.settings.uploads[id][this.editor.language];
   }

   get isFirst() {
      return this.editor.pages.indexOf(this) == 0;
   }

   get useAllFilters():boolean {
      return _.isUndefined(this.settings.photoSettings.filters)
         || this.settings.photoSettings.filters == null;
   }

   set useAllFilters(val:boolean) {
      if (val) {
         delete this.settings.photoSettings.filters;
      } else {
         this.settings.photoSettings.filters = [];
      }
   }

   onClickSelectFilters() {
      (this.editor as AppEditorModel).$uibModal.open({
         size: 'md',
         backdrop: 'static',
         templateUrl: selectFiltersViewUrl,
         controller: 'SelectFiltersController as vm',
         keyboard: false,
         resolve: {
            filters: () => ng.copy(this.settings.photoSettings.filters)
         }
      }).result.then((filters) => {
         if (_.isEmpty(filters)) {
            delete this.settings.photoSettings.filters;
         } else {
            this.settings.photoSettings.filters = filters;
         }
      });
   }

   filterButtonText():string {
      if (this.useAllFilters) {
         return 'All Filters';
      } else if (this.settings.photoSettings.filters.length == 0) {
         return 'Select Filters';
      } else if (this.settings.photoSettings.filters.length == 1) {
         return '1 Filter';
      } else {
         return `${this.settings.photoSettings.filters.length} Filters`;
      }
   }

   getSendTo(prop) {
      // The 'sendTo' value in {prop} settings is an array: ['email'], ['sms'], or ['email', 'sms'].
      // Convert the 'sendTo' array to its string representation and select the corresponding radio button.
      // 'prop' refers to either 'photoSettings' or 'videoSettings'.

      const sendToArray = this.settings[prop].sendTo;
      let selectedSendToValue = 'email,sms';  // Default to both

      // Check the array and set the corresponding string value
      if (ng.equals(sendToArray, ['email'])) {
         selectedSendToValue = 'email';
      } else if (ng.equals(sendToArray, ['sms'])) {
         selectedSendToValue = 'sms';
      }

      this['selectedSendTo'] = selectedSendToValue;
      this.setSendTo(prop, selectedSendToValue);
   }
   
   setSendTo(prop, value) {
      // Convert the string value to an array and store it in the 'sendTo' property
      // of the specified prop, either 'photoSettings' or 'videoSettings'.

      this.settings[prop].sendTo = value.split(',');  // Convert string to array for storage
   }
}
